import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Film, ChevronLeft, ChevronRight } from 'lucide-react';
import { MediaBin } from './MediaBin';
import { UploadDropzone } from './UploadDropzone';
import { WorkspaceDropdown } from './WorkspaceDropdown';


const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 240;
const MAX_WIDTH = 700;
const COLLAPSED_WIDTH = 48;

export function MaterialPanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const lastWidthRef = useRef(DEFAULT_WIDTH);
  const draggingRef = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = width;

    function onMouseMove(ev: MouseEvent) {
      if (!draggingRef.current) return;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (ev.clientX - startX)));
      setWidth(newWidth);
      lastWidthRef.current = newWidth;
    }

    function onMouseUp() {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [width]);

  const togglePanel = useCallback(() => {
    if (expanded) {
      lastWidthRef.current = width;
      setExpanded(false);
    } else {
      setWidth(lastWidthRef.current);
      setExpanded(true);
    }
  }, [expanded, width]);

  // Keep lastWidthRef in sync
  useEffect(() => {
    if (expanded) lastWidthRef.current = width;
  }, [expanded, width]);

  if (!expanded) {
    return (
      <div
        className="flex flex-col shrink-0 border-e border-[#2A2A2E] bg-[#1A1A1E]"
        style={{ width: COLLAPSED_WIDTH }}
      >
        {/* Expand toggle */}
        <button
          onClick={togglePanel}
          className="p-2 flex justify-center hover:bg-[var(--user-nav-hover-bg)] transition-colors"
          title={t('editor.collapsePanel', 'Show/hide material panel')}
        >
          <ChevronRight size={16} className="opacity-60" />
        </button>

        {/* Media icon */}
        <div className="flex flex-col items-center gap-1 pt-1">
          <button
            onClick={togglePanel}
            className="p-2 rounded-[var(--radius-chip)] text-[var(--user-page-accent)] opacity-100 hover:bg-[var(--user-nav-hover-bg)] transition-all"
            title={t('editor.media', 'Media')}
          >
            <Film size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className="relative flex shrink-0 border-e border-[#2A2A2E]"
      style={{ width }}
    >
      {/* Panel content */}
      <div className="flex flex-col flex-1 min-w-0 bg-[#1A1A1E]">
        <div className="flex-1 flex flex-col min-h-0 p-2 gap-2">
          <WorkspaceDropdown />
          <UploadDropzone />
          <div className="flex-1 min-h-0">
            <MediaBin />
          </div>
        </div>
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute top-0 end-0 w-1 h-full cursor-col-resize hover:bg-[var(--user-page-accent)]/30 transition-colors z-10"
      />

      {/* Collapse toggle */}
      <button
        onClick={togglePanel}
        className="absolute top-1/2 -end-3 -translate-y-1/2 w-3 h-8 flex items-center justify-center rounded-e bg-[#1A1A1E] border border-s-0 border-[#2A2A2E] hover:bg-[var(--user-nav-hover-bg)] transition-colors z-20 cursor-pointer"
        title={t('editor.collapsePanel', 'Show/hide material panel')}
      >
        <ChevronLeft size={10} className="opacity-60" />
      </button>

    </div>
  );
}
